/**
 * D855 (owner Q3, 2026-10-30): the live example calculator of `fare.finalRevision` in Regras governadas. The operator types a
 * quoted trip, the trip as driven and a simple tariff (per km and per minute); the console runs the SAME engine a ride runs
 * (`@vello/fare-quote` `simulateFinalFareRevision`) on the rule being edited and shows the final fare, why it moved, which
 * trigger fired and which limit held it — so every knob can be tried before it is proposed.
 *
 * Amounts are example values with two decimals (no currency is named: it is a what-if, never a charge). Distances are typed
 * in kilometres and times in minutes only here; the engine works in metres and seconds. Neutral: no country or currency.
 */
import { finalFarePolicyIssue, simulateFinalFareRevision, type FinalFarePolicyValue, type FinalFareRule, type RouteRevisionOutcome } from '@vello/fare-quote';

/** The calculator's inputs as typed (text, so a half-typed number is never lost). `category` '' = the default rule. */
export interface FinalFareExampleForm {
  readonly quoted: string;
  readonly quotedKm: string;
  readonly quotedMinutes: string;
  readonly drivenKm: string;
  readonly drivenMinutes: string;
  readonly perKm: string;
  readonly perMinute: string;
  readonly distanceEvidenced: boolean;
  readonly category: string;
}

/** The trip the calculator opens with (the "Ler mais" examples: 10 km and 20 minutes quoted at 32.00, driven 13 km). */
export const FINAL_FARE_EXAMPLE_START: FinalFareExampleForm = {
  quoted: '32.00',
  quotedKm: '10',
  quotedMinutes: '20',
  drivenKm: '13',
  drivenMinutes: '20',
  perKm: '2.00',
  perMinute: '0.40',
  distanceEvidenced: true,
  category: '',
};

export type FinalFareExampleField = Exclude<keyof FinalFareExampleForm, 'distanceEvidenced' | 'category'>;

export type FinalFareExampleView =
  /** The rule being edited is not a complete, valid value yet (the form shows its own issues). */
  | { readonly kind: 'incomplete' }
  /** One input is not a number of zero or more. */
  | { readonly kind: 'bad_input'; readonly field: FinalFareExampleField }
  | {
      readonly kind: 'ok';
      readonly quotedMinor: bigint;
      readonly finalMinor: bigint;
      /** final − quoted (negative when the fare came down). */
      readonly differenceMinor: bigint;
      readonly direction: RouteRevisionOutcome['direction'];
      readonly reasonCode: string;
      readonly triggerCode: string;
      readonly capCode?: string;
    };

/** A typed decimal (`32,00` or `32.00`), or undefined when it is not a number of zero or more. */
export function parseExampleNumber(text: string): number | undefined {
  const t = text.trim().replace(',', '.');
  if (!/^\d+(?:\.\d+)?$/.test(t)) return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** The categories the rule names (for the "which rule" choice; the default rule is always offered). */
export function finalFareExampleCategories(value: unknown): readonly string[] {
  if (typeof value !== 'object' || value === null) return [];
  const byCategory = (value as { byCategory?: unknown }).byCategory;
  return typeof byCategory === 'object' && byCategory !== null ? Object.keys(byCategory).sort() : [];
}

/** What the rule being edited decides for the example trip. */
export function finalFareExampleView(value: unknown, form: FinalFareExampleForm): FinalFareExampleView {
  if (finalFarePolicyIssue(value) !== undefined) return { kind: 'incomplete' };
  const policy = value as FinalFarePolicyValue;
  const rule: FinalFareRule = (form.category !== '' ? policy.byCategory[form.category] : undefined) ?? policy.default;
  const fields: readonly FinalFareExampleField[] = ['quoted', 'quotedKm', 'quotedMinutes', 'drivenKm', 'drivenMinutes', 'perKm', 'perMinute'];
  const n: Partial<Record<FinalFareExampleField, number>> = {};
  for (const f of fields) {
    const parsed = parseExampleNumber(form[f]);
    if (parsed === undefined) return { kind: 'bad_input', field: f };
    n[f] = parsed;
  }
  const num = (f: FinalFareExampleField): number => n[f] as number;
  const outcome = simulateFinalFareRevision(rule, {
    quotedMinor: Math.round(num('quoted') * 100),
    quotedDistanceMeters: Math.round(num('quotedKm') * 1_000),
    quotedDurationSeconds: Math.round(num('quotedMinutes') * 60),
    executedDistanceMeters: Math.round(num('drivenKm') * 1_000),
    executedDurationSeconds: Math.round(num('drivenMinutes') * 60),
    distanceEvidenced: form.distanceEvidenced,
    perKilometerMinor: Math.round(num('perKm') * 100),
    perMinuteMinor: Math.round(num('perMinute') * 100),
  });
  const quotedMinor = BigInt(Math.round(num('quoted') * 100));
  return {
    kind: 'ok',
    quotedMinor,
    finalMinor: outcome.finalMinor,
    differenceMinor: outcome.finalMinor - quotedMinor,
    direction: outcome.direction,
    reasonCode: outcome.reasonCode,
    triggerCode: outcome.triggerCode,
    ...(outcome.capCode !== undefined ? { capCode: outcome.capCode } : {}),
  };
}
